import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaChartBar,
  FaThumbtack,
  FaCog,
  FaTrash,
  FaSignOutAlt,
} from "react-icons/fa";
import { Dashboard } from "@/models/dashboard";
import { useSidebarContext } from "@/hooks/use-sidebar";
import logger from "@/utils/logger";
import { useIsDashboardActive } from "@/hooks/use-dashboard-navigation";
import { createDashboardDragPayload, DRAG_TYPES } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem, { SidebarItemAction } from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";
import { ContextMenuItem, ContextMenuDivider } from "./context-menu-helpers";

// Data transfer type for dashboard drag & drop (legacy, keep for backward compatibility)
export const DASHBOARD_DRAG_TYPE = DRAG_TYPES.DASHBOARD;

// Drop mode: 'reorder' for moving between items, 'group' for creating a group
export type DropMode = "reorder" | "group";

interface DashboardItemProps {
  dashboard: Dashboard;
  isPinned?: boolean;
  onUnpin?: () => void;
  // Drag & drop props for reordering within sidebar (legacy - kept for compatibility)
  onDragStart?: (e: React.DragEvent, dashboardId: string) => void;
  onDragOver?: (e: React.DragEvent, dashboardId: string, dropMode: DropMode) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, dashboardId: string, dropMode: DropMode) => void;
  isDragging?: boolean;
  isDragOver?: boolean;
  // Drop mode indicator (reorder = line indicator, group = full highlight)
  dropMode?: DropMode;
  // Enable dragging to main content for split view
  enableDragToContent?: boolean;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all dashboard IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
  // Drag-to-group props
  /** Callback when another dashboard is dropped on this one to create a group */
  onDropToCreateGroup?: (draggedDashboardId: string, targetDashboardId: string) => void;
  /** Callback to remove this dashboard from its current group */
  onRemoveFromGroup?: () => void;
}

const DashboardItem: React.FC<DashboardItemProps> = ({
  dashboard,
  isPinned = false,
  onUnpin,
  enableDragToContent = true,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
  onDropToCreateGroup,
  onRemoveFromGroup,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();
  const isActiveFromUrl = useIsDashboardActive(dashboard.id);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  
  const {
    getItemConfig,
    pinItem,
    renamingDashboardId,
    cancelRenamingDashboard,
    renameDashboard,
    deleteDashboard,
    hasUnsavedChanges,
  } = useSidebarContext();

  const isRenaming = renamingDashboardId === dashboard.id;
  
  // Get pinned status from sidebar config if not explicitly passed
  const itemConfig = getItemConfig(dashboard.id!);
  const isItemPinned = isPinned || itemConfig?.pinned || false;
  const dashboardColor = dashboard.color || "var(--arc-surface-tertiary)";
  
  // Check if dashboard has unsaved changes
  const dashboardHasUnsavedChanges = dashboard.id ? hasUnsavedChanges(dashboard.id) : false;

  // Create drag payload
  const dragPayload = createDashboardDragPayload(
    dashboard.id!,
    dashboard.name || "Dashboard",
    dashboardColor
  );

  const handleClick = useCallback(() => {
    navigateTo(`/dashboards?id=${dashboard.id}`, { title: dashboard.name || "Dashboard" });
  }, [dashboard.id, dashboard.name, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRenameSubmit = useCallback(
    async (newName: string) => {
      try {
        await renameDashboard(dashboard.id!, newName);
        cancelRenamingDashboard();
      } catch (error) {
        logger.error("Failed to rename dashboard:", error);
      }
    },
    [dashboard.id, renameDashboard, cancelRenamingDashboard]
  );

  const handleTogglePin = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isItemPinned) {
      onUnpin?.();
    }
    pinItem(dashboard.id!, "dashboard", !isItemPinned);
  }, [onUnpin, pinItem, dashboard.id, isItemPinned]);

  const handleConfig = useCallback(() => {
    navigateTo(`/dashboards?id=${dashboard.id}&tab=settings`, { title: `${dashboard.name || "Dashboard"} - Settings` });
  }, [dashboard.id, dashboard.name, navigateTo]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteDashboard(dashboard.id!);
      navigateTo("/dashboards", { title: "Dashboards" });
    } catch (error) {
      logger.error("Failed to delete dashboard:", error);
    }
  }, [dashboard.id, deleteDashboard, navigateTo]);

  // Build hover actions
  const hoverActions = (
    <>
      <SidebarItemAction
        onClick={handleTogglePin}
        title={isItemPinned ? "Unpin" : "Pin"}
        icon={<FaThumbtack size={10} className={isItemPinned ? "text-yellow-400" : ""} />}
      />
      <SidebarItemAction
        onClick={handleConfig}
        title="Configure"
        icon={<FaCog size={10} />}
      />
      <SidebarItemAction
        onClick={handleDelete}
        title="Delete"
        icon={<FaTrash size={10} />}
        variant="danger"
      />
    </>
  );

  // Unsaved changes indicator
  const nameIndicator = dashboardHasUnsavedChanges ? (
    <div
      className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0 animate-pulse"
      title="Modifications non sauvegardées"
    />
  ) : null;

  // Pinned indicator (shown when not hovered)
  const statusIndicator = isItemPinned ? (
    <FaThumbtack size={10} className="arc-sidebar-pin-icon" />
  ) : null;

  // Additional context menu items for dashboard
  const additionalContextMenuItems = (
    <>
      <ContextMenuItem onClick={() => { handleTogglePin(); closeContextMenu(); }}>
        <FaThumbtack className={isItemPinned ? "text-yellow-400" : ""} />
        {isItemPinned ? "Désépingler" : "Épingler"}
      </ContextMenuItem>
      <ContextMenuItem onClick={() => { handleConfig(); closeContextMenu(); }}>
        <FaCog className="text-gray-400" /> Configurer
      </ContextMenuItem>
      {onRemoveFromGroup && (
        <>
          <ContextMenuDivider />
          <ContextMenuItem onClick={() => { onRemoveFromGroup(); closeContextMenu(); }}>
            <FaSignOutAlt /> Retirer du dossier
          </ContextMenuItem>
        </>
      )}
      <ContextMenuDivider />
      <ContextMenuItem onClick={() => { handleDelete(); closeContextMenu(); }} danger>
        <FaTrash /> Supprimer
      </ContextMenuItem>
    </>
  );

  return (
    <>
      <SidebarItem
        id={dashboard.id!}
        name={dashboard.name || "Untitled Dashboard"}
        resourceType="dashboard"
        icon={<FaChartBar size={14} />}
        color={dashboardColor}
        showColorIndicator={true}
        isActive={isActiveFromUrl}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={enableDragToContent}
        onDropToCreateGroup={onDropToCreateGroup}
        dragPayload={dragPayload}
        isRenaming={isRenaming}
        onRenameSubmit={handleRenameSubmit}
        onRenameCancel={cancelRenamingDashboard}
        hoverActions={hoverActions}
        nameIndicator={nameIndicator}
        statusIndicator={statusIndicator}
      />

      {/* Context Menu - unified with ResourceContextMenu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
            additionalItemsAfter={additionalContextMenuItems}
          />
        )}
      </AnimatePresence>
    </>
  );
};

DashboardItem.displayName = "DashboardItem";

export default DashboardItem;
